import React, {ReactNode} from "react";
import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import PortalDialogType from "../../PortalDialogType";
import {Dialog} from "primereact/dialog";
import Dropzone, {DropzoneGlobals, DropzoneMode} from "../../../startscreen/Dropzone";
import {Button} from "primereact/button";
import ProgressCircle from "../../../../shared/components/ProgressCircle";
import FileHelper from "../../../../shared/helper/FileHelper";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import {UrlConverterDialogDefinition} from "./UrlConverterDialog";
import {OcrDialogDefinition} from "../convert/OcrDialog";
import {PdfaDialogDefinition} from "../convert/PdfaDialog";
import {ConvertDialogDefinition} from "../convert/ConvertDialog";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import Parameter from "../../parameter/Parameter";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {
	Functions,
	Information,
	Limits,
	Pdfa,
	PdfaInterface,
	PdfaLevel,
	SignatureEditProfile,
	UserCredentials
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalConverter} from "../../../../shared/models/PortalConverter";
import ProgressBar from "../../../../shared/components/ProgressBar";
import {PortalDocumentManagerGlobals} from "../../../document/PortalDocumentManager";
import FileSizeUnit from "../../../../shared/units/FileSizeUnit";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import Exception from "../../../../shared/exception/Exception";
import PortalSession from "../../../session/PortalSession";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import ContentMode from "../../../ContentMode";

interface UploadEventFile {
	id: string;
	name: string;
	progress: number;
	total: number;
	bytes: number;
	current: boolean;
}

interface UploadEvent {
	files: Array<UploadEventFile>;
	progress: number;
	total: number;
	bytes: number;
}

interface UploadDialogProperties extends PortalDialogProperties<any>, DropzoneGlobals, PortalDocumentManagerGlobals {
	uploadFiles?: FileList;
	contentMode: ContentMode;
}

interface UploadDialogState extends PortalDialogState<any> {
	uploadEvent?: UploadEvent;
	queue: FileList | null;
}

export class UploadDialogDefinition extends PortalDialog<any, UploadDialogProperties, UploadDialogState> {
	private abortController: AbortController;

	constructor(props: Readonly<UploadDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			queue: null
		};

		this.onProgress = this.onProgress.bind(this);
		this.onUploadDone = this.onUploadDone.bind(this);
		this.onUploadStart = this.onUploadStart.bind(this);
		this.onError = this.onError.bind(this);
		this.abortController = new AbortController();
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(portalInfo.functionsStatus?.uploadFile, Functions.getUploadFileDefault());
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		return this.isEnabled(portalInfo);
	}

	private onProgress(event: any): void {
		if (!FunctionHelper.exists(this.state.uploadEvent)) {
			return;
		}

		let bytes: number = 0;
		let currentEvent: UploadEvent = this.state.uploadEvent!;

		for (let eventFile of currentEvent.files) {
			eventFile.current = false;

			if (eventFile.id === event.id) {
				eventFile.bytes = event.loaded;
				eventFile.progress = Math.min(
					Math.floor(eventFile.bytes / (eventFile.total / 100)), 100
				);
				eventFile.current = true;
			}

			bytes += eventFile.bytes;
		}

		currentEvent.bytes = bytes;
		currentEvent.progress = Math.min(
			Math.floor(currentEvent.bytes / (currentEvent.total / 100)), 100
		);

		this.setState({
			uploadEvent: currentEvent
		});
	}

	private onUploadDone(): void {
		this.clearUploadEvents();
		this.hide();

		let selectedDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		let portalInfo: Information = this.props.context.info;

		switch (this.props.dropzoneMode) {
			case DropzoneMode.ARCHIVE:
				for (let selectedDocument of selectedDocuments) {
					if (selectedDocument.isArchive()) {
						this.props.context.documentManager.unselect(
							selectedDocument.getSourceDocumentId()!
						);
					}
				}

				selectedDocuments = this.props.context.documentManager.getSelection();
				if (selectedDocuments.length === 0) {
					break;
				}

				let convertParameter: Parameter<PortalConverter> = DialogDefault.getParameter(
					PortalDialogType.CONVERT, selectedDocuments
				);
				convertParameter.data.archive = true;
				convertParameter.data.pdfa = Pdfa.fromJson({
					convert: {
						level: PdfaLevel._3b
					}
				} as PdfaInterface);

				this.props.context.getPage().setState({
					showConvertDialog: true,
					convertParameter: convertParameter
				});
				break;
			case DropzoneMode.CONVERT:
				for (let selectedDocument of selectedDocuments) {
					if (!ConvertDialogDefinition.isActive(
							[selectedDocument], portalInfo, this.props.signatureEditProfile
						) ||
						selectedDocument.getMimeType() === MimeTypes.PDF) {
						this.props.context.documentManager.unselect(
							selectedDocument.getSourceDocumentId()!
						);
					}
				}

				selectedDocuments = this.props.context.documentManager.getSelection();
				if (selectedDocuments.length === 0) {
					break;
				}

				this.props.context.getPage().setState({
					showConvertDialog: true,
					convertParameter: undefined
				});
				break;
			case DropzoneMode.OCR:
				for (let selectedDocument of selectedDocuments) {
					if (!OcrDialogDefinition.isActive(
						[selectedDocument], portalInfo, this.props.signatureEditProfile
					)) {
						this.props.context.documentManager.unselect(
							selectedDocument.getSourceDocumentId()!
						);
					}
				}

				selectedDocuments = this.props.context.documentManager.getSelection();
				if (selectedDocuments.length === 0) {
					break;
				}

				this.props.context.getPage().setState({
					showOcrDialog: true,
					ocrParameter: undefined
				});
				break;
		}
	}

	private onUploadStart(files: FileList): void {
		this.abortController = new AbortController();

		this.setState({
			queue: files,
			uploadEvent: this.buildUploadEvent(files)
		}, this.uploadFiles);
	}

	private renderFilelist(): ReactNode {
		let filelist: Array<ReactNode> = [];
		let currentEvent: UploadEvent | undefined = this.state.uploadEvent;

		for (let file of FunctionHelper.applyValue(currentEvent?.files, [])) {
			if (file.progress === 100 || file.bytes === file.total) {
				continue;
			}

			filelist.push(
				<li style={file.current ? {fontWeight: "bold"} : {}} key={file.name + file.total}>
					{file.name}<br/>
					{FileHelper.formatFileSize(file.bytes, 2)} /
					{FileHelper.formatFileSize(file.total, 2)}
				</li>
			)
		}

		return (
			<ul className="filelist">
				{filelist}
			</ul>
		);
	}

	/**
	 * render component as react node
	 */
	public renderDialog(): ReactNode {
		let currentEvent: UploadEvent | undefined = this.state.uploadEvent;
		let user: UserCredentials | undefined = this.props.context.user;
		let uploadLimit: number = FunctionHelper.applyValue(
			user?.userLimits?.uploadLimit, Limits.getUploadLimitDefault()
		);
		let diskSpaceLimit: number = FunctionHelper.applyValue(
			user?.userLimits?.diskSpaceLimit, Limits.getDiskSpaceLimitDefault()
		);
		let maxFiles: number = FunctionHelper.applyValue(user?.userLimits?.maxFiles, Limits.getMaxFilesDefault());
		let info: Information = this.props.context.info;

		let portalDocuments: Array<PortalDocumentInterface> = this.props.portalDocuments.get();
		let uploadedFiles: number = portalDocuments.length;
		let uploadedFileSize: number = 0;
		for (let portalDocument of portalDocuments) {
			uploadedFileSize += portalDocument.size;
		}

		let classList: Array<string> = ["upload-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<fieldset className="p-col-12 p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.upload.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<div className="p-col-12" hidden={
						FunctionHelper.exists(currentEvent) || !info.userInterface?.limits?.displayMaxFiles ||
						maxFiles === 0
					}>
						<ProgressBar unit=""
									 label={Translate.value(
										 "startscreen.panels.documentmanager.limits.documents",
										 {
											 ns: "screen",
											 value: uploadedFiles,
											 max: maxFiles
										 }
									 )}
									 bars={[
										 {value: (uploadedFiles / maxFiles) * 100}
									 ]}/>
					</div>
					<div className="p-col-12"
						 hidden={
							 FunctionHelper.exists(currentEvent) || !info.userInterface?.limits?.displayDiskSpace ||
							 diskSpaceLimit === 0
						 }>
						<ProgressBar unit=""
									 label={
										 Translate.value(
											 "startscreen.panels.documentmanager.limits.workspace",
											 {
												 ns: "screen",
												 value: FileHelper.formatFileSize(uploadedFileSize),
												 max: FileHelper.formatFileSize(diskSpaceLimit * 1024 * 1024)
											 }
										 )}
									 bars={[
										 {
											 value: (
												 FileHelper.formatFileSizeAs(FileSizeUnit.MEGABYTES, uploadedFileSize) /
												 diskSpaceLimit
											 ) * 100
										 }
									 ]}/>
					</div>
					<div className="p-col-12" hidden={FunctionHelper.exists(currentEvent)}>
						<Dropzone {...this.props} context={this.props.context} onError={this.onError}
								  onUploadStart={(files: FileList) => {
									  this.props.context.getPage().setState({
										  dropzoneMode: DropzoneMode.UPLOAD
									  });
									  this.onUploadStart(files);
								  }}>
							<p>
								<Translate {...this.props}
										   data="startscreen.panels.documentmanager.dropzone.description"
										   namespace="screen"
										   values={{
											   mode: Translate.value(
												   "startscreen.panels.documentmanager.dropzone.upload",
												   {ns: "screen"}
											   )
										   }}/>
								<br/><br/>
								<Button label={Translate.value("buttons.browse")}/>
								<br/><br/>
								{uploadLimit !== 0 &&
									<Translate {...this.props}
											   data="startscreen.panels.documentmanager.dropzone.maxsize"
											   values={{size: FileHelper.formatFileSize(uploadLimit * 1024 * 1024)}}
											   namespace="screen"/>
								}
							</p>
						</Dropzone>
						<div className="functions p-grid">
							{ConvertDialogDefinition.isEnabled(this.props.context.info) &&
								<div className="p-col-3">
									<Dropzone {...this.props} context={this.props.context}
											  onError={this.onError} className="no-border"
											  tooltip={Translate.value("upload.converter", {ns: "dialog"})}
											  onUploadStart={(files: FileList) => {
												  this.props.context.getPage().setState({
													  dropzoneMode: DropzoneMode.CONVERT
												  });
												  this.onUploadStart(files);
											  }}>
										<Button icon="icon icon-convert multiple"/>
									</Dropzone>
								</div>
							}
							{PdfaDialogDefinition.isEnabled(this.props.context.info) &&
								<div className="p-col-3">
									<Dropzone {...this.props} context={this.props.context}
											  onError={this.onError} className="no-border"
											  tooltip={Translate.value("upload.pdfa", {ns: "dialog"})}
											  onUploadStart={(files: FileList) => {
												  this.props.context.getPage().setState({
													  dropzoneMode: DropzoneMode.ARCHIVE
												  });
												  this.onUploadStart(files);
											  }}>
										<Button icon="icon icon-archive multiple"/>
									</Dropzone>
								</div>
							}
							{OcrDialogDefinition.isEnabled(this.props.context.info) &&
								<div className="p-col-3">
									<Dropzone {...this.props} context={this.props.context}
											  onError={this.onError} className="no-border"
											  tooltip={Translate.value("upload.ocr", {ns: "dialog"})}
											  onUploadStart={(files: FileList) => {
												  this.props.context.getPage().setState({
													  dropzoneMode: DropzoneMode.OCR
												  });
												  this.onUploadStart(files);
											  }}>
										<Button icon="icon icon-ocr multiple"/>
									</Dropzone>
								</div>
							}
							{UrlConverterDialogDefinition.isEnabled(this.props.context.info) &&
								<div className="p-col-3">
									<Button icon="icon icon-globe multiple"
											tooltip={Translate.value("upload.url", {ns: "dialog"})}
											onClick={() => {
												this.hide();
												this.props.context.getPage().setState({
													showUrlConverterDialog: true
												});
											}}/>
								</div>
							}
						</div>
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.upload.functions"} namespace={"help"}
									   markdown={true}/>
						</Helper>
					</div>
					<div className="p-col-12 upload-info p-grid" hidden={!FunctionHelper.exists(currentEvent)}
						 style={{flexWrap: "nowrap"}}>
						<div className="p-col-fixed">
							<ProgressCircle progress={FunctionHelper.applyValue(currentEvent?.progress, 0)}
											layers={[
												(options: { centerX: number, centerY: number }) => {
													return (
														<text x={options.centerX} y={options.centerY}
															  textAnchor="middle"
															  dominantBaseline="central"
															  style={{
																  fontSize: '18px'
															  }}>
															{FunctionHelper.applyValue(
																currentEvent?.progress, 0
															) + "%"}
														</text>
													)
												}
											]}/>
						</div>
						<div className="p-col" style={{width: 0}}>
							{this.renderFilelist()}
						</div>
					</div>
				</fieldset>
			</Dialog>
		);
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		// keydown events can be ignored
	}

	protected async onError(exception: Exception): Promise<boolean> {
		this.setState({
			exception: undefined
		});

		if (exception.code === ErrorCode.ERR_CANCELED) {
			return false;
		}

		if (await super.onError(exception)) {
			return true;
		}

		this.abortController?.abort();

		if (FunctionHelper.exists(this.state.uploadEvent)) {
			this.props.context.documentManager.sync();
		}

		this.clearUploadEvents();

		return false;
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-upload small"}/>
					<Translate {...this.props} data="upload.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => {
					this.abortController?.abort();

					if (FunctionHelper.exists(this.state.uploadEvent)) {
						this.props.context.documentManager.sync();
					}

					this.clearUploadEvents();
					this.hide();
				}}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
							onClick={() => {
								this.abortController?.abort();

								if (FunctionHelper.exists(this.state.uploadEvent)) {
									this.props.context.documentManager.sync();
								}

								this.clearUploadEvents();
								this.hide();
							}}
							className="dialog-button"/>
				</div>
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		if (FunctionHelper.exists(this.props.uploadFiles)) {
			this.onUploadStart(this.props.uploadFiles!);
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.UPLOAD;
	}

	protected getDefaultParameter(): undefined {
		return;
	}

	protected isEditPasswordNeeded(): boolean {
		return false; // Don't check for password
	}

	private buildUploadEvent(files: FileList): UploadEvent {
		let uploadEvent: UploadEvent = {
			files: [] as Array<UploadEventFile>,
			progress: 0,
			total: 0,
			bytes: 0
		} as UploadEvent;

		for (let file of files) {
			let eventFile: UploadEventFile = {
				id: file.name + Date.now(),
				name: file.name,
				bytes: 0,
				progress: 0,
				total: file.size,
				current: false
			};

			(file as any).id = eventFile.id;

			uploadEvent.files.push(eventFile);
			uploadEvent.total += eventFile.total;
		}

		return uploadEvent;
	}

	private async uploadFiles(): Promise<void> {
		if (!FunctionHelper.exists(this.state.queue)) {
			return;
		}

		let session: PortalSession = this.props.context.session;

		try {
			await session.getDocumentManager().uploadDocuments(this.state.queue!, {
				onProgress: this.onProgress,
				abortSignal: this.abortController.signal,
				open: this.props.contentMode === ContentMode.VIEWER && this.props.dropzoneMode === DropzoneMode.UPLOAD
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.uploadFiles();
			}

			return;
		}

		if (this.state.queue!.length > 1 && this.props.contentMode === ContentMode.VIEWER &&
			this.props.dropzoneMode !== DropzoneMode.UPLOAD) {
			this.props.context.getPage().setState({
				contentMode: ContentMode.STARTSCREEN
			});
		}

		this.onUploadDone();
	}

	private clearUploadEvents() {
		this.setState({
			uploadEvent: undefined,
			queue: null
		});
		this.props.context.getPage().setState({
			uploadFiles: undefined
		});
	}
}

const UploadDialog = React.memo(UploadDialogDefinition,
	(prevProps: Readonly<UploadDialogProperties>, nextProps: Readonly<UploadDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true;
		}

		return false;
	}
);

export default UploadDialog;